import React, { useState, useEffect, useRef } from 'react';
import { Card, Input, Button, List, Select, Empty, Typography, Upload, App } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { agentAPI, projectAPI } from '../services/api';
import { formatDateTimeBeijing } from '../utils/datetime';
import ReactMarkdown from 'react-markdown';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const AgentWorkspace = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { agentType: urlAgentType } = useParams();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [agentType, setAgentType] = useState(urlAgentType || 'scheme_writer');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [province, setProvince] = useState('天津市');
  const [location, setLocation] = useState('天津市滨海新区');
  const [schemeContent, setSchemeContent] = useState('');
  const [fileList, setFileList] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (urlAgentType && urlAgentType !== agentType) {
      setAgentType(urlAgentType);
      setMessages([]);
    }
  }, [urlAgentType, agentType]);

  useEffect(() => {
    loadProjects();
    loadConversations();
  }, [agentType]);

  useEffect(() => {
    if (selectedProject) {
      const project = projects.find(project => project.id === selectedProject);
      if (project) {
        if (project.province) {
          setProvince(project.province);
        }
        if (project.location) {
          setLocation(project.location);
        }
      }
    }
  }, [selectedProject, projects]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleAgentTypeChange = (value) => {
    setAgentType(value);
    setMessages([]);
    navigate(`/agent/${value}`);
  };

  const handleConversationClick = async (conversation) => {
    try {
      const response = await agentAPI.getConversation(conversation.id);
      if (response.data && response.data.messages) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      message.error('加载对话失败');
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectAPI.list();
      setProjects(response.data);
    } catch (error) {
      message.error('加载项目失败');
    }
  };

  const loadConversations = async () => {
    try {
      const response = await agentAPI.getConversations();
      setConversations(response.data);
    } catch (error) {
      console.error('加载对话历史失败');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = { role: 'user', content: inputValue };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setLoading(true);

    try {
      const requestData = {
        messages: newMessages,
        agent_type: agentType,
        project_id: selectedProject
      };

      if (agentType === 'scheme_reviewer') {
        requestData.scheme_review_params = {
          province: province,
          location: location,
          scheme_content: schemeContent
        };
      }

      const assistantMessage = { role: 'assistant', content: '' };
      setMessages([...newMessages, assistantMessage]);

      agentAPI.chatStream(
        requestData,
        (chunk) => {
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: updated[updated.length - 1].content + chunk
            };
            return updated;
          });
        },
        (conversationId) => {
          loadConversations();
        },
        (error) => {
          message.error(error || '发送失败');
          setMessages(newMessages);
        }
      );
    } catch (error) {
      message.error(error.response?.data?.error || '发送失败');
      setMessages(newMessages);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (info) => {
    const { file } = info;
    const reader = new FileReader();
    reader.onload = (e) => {
      setSchemeContent(e.target.result);
    };
    reader.readAsText(file);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getAgentTypeName = (type) => {
    const types = {
      'scheme_writer': '方案编写助手',
      'scheme_reviewer': '方案审核助手',
      'qa_assistant': '智能问答助手'
    };
    return types[type] || type;
  };

  const getAgentDescription = (type) => {
    const descriptions = {
      'scheme_writer': 'AI辅助方案编写',
      'scheme_reviewer': 'AI辅助方案审核',
      'qa_assistant': '智能问答助手'
    };
    return descriptions[type] || 'AI辅助方案编写与审核';
  };

  const markdownStyles = {
    h1: { fontSize: '24px', fontWeight: 'bold', marginBottom: '16px', marginTop: '16px', color: '#1890ff' },
    h2: { fontSize: '20px', fontWeight: 'bold', marginBottom: '12px', marginTop: '12px', color: '#1890ff' },
    h3: { fontSize: '18px', fontWeight: 'bold', marginBottom: '8px', marginTop: '8px', color: '#1890ff' },
    h4: { fontSize: '16px', fontWeight: 'bold', marginBottom: '8px', marginTop: '8px' },
    p: { marginBottom: '12px', lineHeight: '1.6' },
    ul: { paddingLeft: '20px', marginBottom: '12px' },
    ol: { paddingLeft: '20px', marginBottom: '12px' },
    li: { marginBottom: '8px', lineHeight: '1.6' },
    strong: { color: '#1890ff', fontWeight: 'bold' },
    code: { 
      backgroundColor: '#f5f5f5', 
      padding: '2px 6px', 
      borderRadius: '4px', 
      fontFamily: 'monospace',
      fontSize: '14px'
    },
    blockquote: { 
      borderLeft: '4px solid #1890ff', 
      paddingLeft: '12px', 
      fontStyle: 'italic', 
      color: '#666',
      marginBottom: '12px'
    },
    table: { width: '100%', borderCollapse: 'collapse', marginBottom: '12px' },
    th: { 
      backgroundColor: '#1890ff', 
      color: 'white', 
      padding: '8px 12px', 
      textAlign: 'left',
      fontWeight: 'bold'
    },
    td: { 
      border: '1px solid #e8e8e8', 
      padding: '8px 12px'
    }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ marginBottom: '24px', flexShrink: 0 }}>
        <Title level={2} style={{ marginBottom: '8px' }}>{getAgentTypeName(agentType)}</Title>
        <Text type="secondary">{getAgentDescription(agentType)}</Text>
      </div>

      <div style={{ display: 'flex', gap: '24px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Card 
          style={{ width: '300px', display: 'flex', flexDirection: 'column', height: '100%' }}
          styles={{ body: { padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
        >
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>智能体类型</Text>
            <Select
              value={agentType}
              onChange={handleAgentTypeChange}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="scheme_reviewer">方案审核助手</Option>
              <Option value="scheme_writer">方案编写助手</Option>
              <Option value="qa_assistant">智能问答助手</Option>
            </Select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>关联项目</Text>
            <Select
              value={selectedProject}
              onChange={setSelectedProject}
              style={{ width: '100%' }}
              placeholder="选择项目"
              allowClear
            >
              {projects.map(project => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </div>

          {agentType === 'scheme_reviewer' && (
            <>
              <div style={{ marginBottom: '24px' }}>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>项目所在省份</Text>
                <Select
                  value={province}
                  onChange={setProvince}
                  style={{ width: '100%' }}
                  size="large"
                >
                  <Option value="天津市">天津市</Option>
                  <Option value="北京市">北京市</Option>
                  <Option value="上海市">上海市</Option>
                  <Option value="重庆市">重庆市</Option>
                  <Option value="河北省">河北省</Option>
                  <Option value="山西省">山西省</Option>
                  <Option value="辽宁省">辽宁省</Option>
                  <Option value="吉林省">吉林省</Option>
                  <Option value="黑龙江省">黑龙江省</Option>
                  <Option value="江苏省">江苏省</Option>
                  <Option value="浙江省">浙江省</Option>
                  <Option value="安徽省">安徽省</Option>
                  <Option value="福建省">福建省</Option>
                  <Option value="江西省">江西省</Option>
                  <Option value="山东省">山东省</Option>
                  <Option value="河南省">河南省</Option>
                  <Option value="湖北省">湖北省</Option>
                  <Option value="湖南省">湖南省</Option>
                  <Option value="广东省">广东省</Option>
                  <Option value="海南省">海南省</Option>
                  <Option value="四川省">四川省</Option>
                  <Option value="贵州省">贵州省</Option>
                  <Option value="云南省">云南省</Option>
                  <Option value="陕西省">陕西省</Option>
                  <Option value="甘肃省">甘肃省</Option>
                  <Option value="青海省">青海省</Option>
                  <Option value="台湾省">台湾省</Option>
                  <Option value="内蒙古自治区">内蒙古自治区</Option>
                  <Option value="广西壮族自治区">广西壮族自治区</Option>
                  <Option value="西藏自治区">西藏自治区</Option>
                  <Option value="宁夏回族自治区">宁夏回族自治区</Option>
                  <Option value="新疆维吾尔自治区">新疆维吾尔自治区</Option>
                  <Option value="香港特别行政区">香港特别行政区</Option>
                  <Option value="澳门特别行政区">澳门特别行政区</Option>
                </Select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>项目具体地点</Text>
                <Input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="请输入项目具体地点"
                  size="large"
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <Text strong style={{ display: 'block', marginBottom: '8px' }}>方案内容</Text>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <Upload
                    beforeUpload={() => false}
                    onChange={handleFileUpload}
                    fileList={fileList}
                    onRemove={() => {
                      setFileList([]);
                      setSchemeContent('');
                    }}
                    accept=".txt,.doc,.docx,.pdf"
                  >
                    <Button icon={<UploadOutlined />}>上传方案文档</Button>
                  </Upload>
                </div>
                <TextArea
                  value={schemeContent}
                  onChange={(e) => setSchemeContent(e.target.value)}
                  placeholder="请输入或粘贴方案内容，或上传文档"
                  autoSize={{ minRows: 6, maxRows: 12 }}
                  style={{ width: '100%' }}
                />
              </div>
            </>
          )}

          <div style={{ flex: 1, overflow: 'auto' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px' }}>历史对话</Text>
            <List
              size="small"
              dataSource={conversations.filter(c => c.agent_type === agentType).slice(0, 10)}
              renderItem={item => (
                <List.Item 
                  style={{ 
                    padding: '8px', 
                    cursor: 'pointer', 
                    borderRadius: '4px', 
                    backgroundColor: '#f5f5f5', 
                    marginBottom: '8px' 
                  }}
                  onClick={() => handleConversationClick(item)}
                >
                  <div style={{ width: '100%' }}>
                    <Text style={{ fontSize: '14px', display: 'block', marginBottom: '4px' }}>
                      {getAgentTypeName(item.agent_type)}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {formatDateTimeBeijing(item.created_at)}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          </div>
        </Card>

        <Card 
          style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}
          styles={{ body: { padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } }}
        >
          <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px', paddingRight: '8px' }}>
            {messages.length === 0 ? (
              <Empty
                description="有什么我能帮你的吗？"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: '96px' }}
              />
            ) : (
              <div>
                {messages.map((message, index) => (
                  <div
                    key={index}
                    style={{ 
                      display: 'flex', 
                      marginBottom: '16px', 
                      alignItems: 'flex-start', 
                      flexDirection: message.role === 'user' ? 'row-reverse' : 'row' 
                    }}
                  >
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '20px', 
                      flexShrink: 0,
                      backgroundColor: message.role === 'user' ? '#1890ff' : '#f5f5f5',
                      marginLeft: message.role === 'user' ? '12px' : '0',
                      marginRight: message.role === 'user' ? '0' : '12px'
                    }}>
                      {message.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
                    </div>
                    <div style={{ 
                      maxWidth: '70%', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      wordBreak: 'break-word',
                      backgroundColor: message.role === 'user' ? '#1890ff' : '#f5f5f5',
                      color: message.role === 'user' ? 'white' : '#333'
                    }}>
                      {message.role === 'assistant' ? (
                        <ReactMarkdown 
                          components={{
                            h1: ({ children }) => <h1 style={markdownStyles.h1}>{children}</h1>,
                            h2: ({ children }) => <h2 style={markdownStyles.h2}>{children}</h2>,
                            h3: ({ children }) => <h3 style={markdownStyles.h3}>{children}</h3>,
                            h4: ({ children }) => <h4 style={markdownStyles.h4}>{children}</h4>,
                            p: ({ children }) => <p style={markdownStyles.p}>{children}</p>,
                            ul: ({ children }) => <ul style={markdownStyles.ul}>{children}</ul>,
                            ol: ({ children }) => <ol style={markdownStyles.ol}>{children}</ol>,
                            li: ({ children }) => <li style={markdownStyles.li}>{children}</li>,
                            strong: ({ children }) => <strong style={markdownStyles.strong}>{children}</strong>,
                            code: ({ inline, children }) => 
                              inline ? 
                                <code style={markdownStyles.code}>{children}</code> :
                                <pre style={{ ...markdownStyles.code, overflow: 'auto' }}><code>{children}</code></pre>,
                            blockquote: ({ children }) => <blockquote style={markdownStyles.blockquote}>{children}</blockquote>,
                            table: ({ children }) => <table style={markdownStyles.table}><tbody>{children}</tbody></table>,
                            thead: ({ children }) => <thead>{children}</thead>,
                            tbody: ({ children }) => <tbody>{children}</tbody>,
                            tr: ({ children }) => <tr>{children}</tr>,
                            th: ({ children }) => <th style={markdownStyles.th}>{children}</th>,
                            td: ({ children }) => <td style={markdownStyles.td}>{children}</td>,
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      ) : (
                        message.content
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={{ flexShrink: 0, display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="输入您的问题或需求..."
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={loading}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={loading}
              size="large"
            >
              发送
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AgentWorkspace;
